import { viewFilters as api } from 'st-app/api';
import { CollectionInfoInstance } from './CollectionInfoInstance';
import { FilterInfoInstance, SharedFilterInstance } from './FilterInfoInstance';
import { createStore, produce } from 'solid-js/store';
import { FilterGroup } from './FilterGroup';
import { UserFilterGroupInstance } from './UserFilterGroupInstance';
import { CheckListPreference, RevCollectionData, UserFilterGroupInfo } from 'st-app/api';

class UserFilterStore {
    allFilters: FilterInfoInstance[] = [];
    allChecklists: RevCollectionData[] = [];
    allGroups: UserFilterGroupInstance[] = [];
    sharedFilters: SharedFilterInstance[] = [];
    checklistPrefs: Record<string, CheckListPreference> = {};
    collapsedGroups: string[] = [];

    constructor() {
        this.init();
    }

    async init() {
        const options = await api.GetOptions();

        setUserFilters('checklistPrefs', options.checkListPreferences);
        setUserFilters('collapsedGroups', options.collapsedGroups);
        setUserFilters('allFilters', options.filters.map(f => new FilterInfoInstance(f)));
        setUserFilters('sharedFilters', options.sharedFilters.map(f => new SharedFilterInstance(f)));
        setUserFilters('allChecklists', options.collections.map(cl => new CollectionInfoInstance(cl)));
        setUserFilters('allGroups', options.groups.map(g => new UserFilterGroupInstance(g)));
    }

    getGroup(group: FilterGroup) {
        for (const filter of this.allFilters) {
            if (group.id && group.id === filter.filterGroup.filterGroupGU) {
                return filter.filterGroup;
            }
            if (group.name[0]() === filter.filterGroup.groupName) {
                return filter.filterGroup;
            }
        }

        return Object.assign(new UserFilterGroupInstance(new UserFilterGroupInfo()), {
            filterGroupGU: group.id,
            groupName: group.name[0](),
            //iconPath: group.iconPath
        });
    }

    isGroupExpanded(id: string) {
        return !this.collapsedGroups.includes(id);
    }

    toggleExpandedGroup(id: string) {
        const expanded = !this.isGroupExpanded(id);

        // Create a hashset of all current groups
        const allGroups = this.allGroups.reduce((obj, group) => {
            obj[group.filterGroupGU || ''] = true;
            return obj;
        }, {} as Record<string, boolean>);

        const collapsedGroups = [
            ...this.collapsedGroups
                // exclude the specified group (to be re-added if enabled)
                .filter(g => g !== id)
                // filter out any groups that don't exist
                .filter(g => allGroups[g])
        ];

        if (!expanded) {
            collapsedGroups.push(id);
        }

        setUserFilters('collapsedGroups', collapsedGroups);
        api.UpdateCollapsedGroups(collapsedGroups);
    }

    updateCollectionGU(entityGU: string, collectionGU: string) {
        setUserFilters(produce(filters => {
            const clPrefs = filters.checklistPrefs;

            if (!clPrefs[entityGU]) {
                clPrefs[entityGU] = new CheckListPreference();
            }

            clPrefs[entityGU].userFilterCollectionGU = collectionGU;
        }));

        api.UpdateCollectionGU(entityGU, collectionGU);
    }

    async acceptSharedFilter(filter: SharedFilterInstance, filterGroupGU?: string) {
        const newFilter = await api.AcceptSharedFilter(filter.filterGU || '', filterGroupGU || '');

        if (newFilter) {
            const filterInst = new FilterInfoInstance(newFilter);

            setUserFilters('sharedFilters', this.sharedFilters.filter(f => f.filterGU !== filter.filterGU));
            setUserFilters('allFilters', [...this.allFilters, filterInst]);

            return filterInst;
        }
    }

    removeSharedFilter(filter: SharedFilterInstance) {
        setUserFilters('sharedFilters', this.sharedFilters.filter(f => f.filterGU !== filter.filterGU));
        api.RemoveSharedFilter(filter.filterGU || '');
    }

    removeFilter(filter: FilterInfoInstance) {

        setUserFilters('allFilters', this.allFilters.filter(f => f.filterGU !== filter.filterGU));
        api.DeleteFilter(filter.filterGU || '');
    }
}

export const [userFilters, setUserFilters] = createStore(new UserFilterStore());
